import { useEffect, useState } from "react";
import { Link } from "../../router";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";
import { advisoryApi, type ClientContextData } from "./advisoryApi";
import { dateOnly, money, pct } from "./format";

export interface PrefillAction {
  componentType: string;
  label: string;
  description: string;
}

const KYC_PILL: Record<string, string> = { approved: "kyc-approved", pending: "kyc-pending", none: "kyc-none" };

/**
 * Right column: what Andersen knows about the client that the advice engine
 * doesn't — holdings and cash across accounts, KYC and risk profile, recent
 * contact, open orders — plus the "Use Andersen data" actions for the page on
 * screen. A prefill only fills the form in the centre; the advisor saves it.
 */
export function ClientContext({
  investorId,
  actions,
  busy,
  error,
  onPrefill,
  editable,
}: {
  investorId: number | null;
  actions: PrefillAction[];
  busy: string | null;
  error: string | null;
  onPrefill: (componentType: string) => void;
  editable: boolean;
}) {
  const [ctx, setCtx] = useState<ClientContextData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!investorId) return;
    advisoryApi
      .context(investorId)
      .then(setCtx)
      .catch((e) => setErr((e as Error).message));
  }, [investorId]);

  if (!investorId) return <aside className="adv-context"><p className="muted">This advice has no Andersen client.</p></aside>;

  return (
    <aside className="adv-context" aria-label="Client context">
      {editable && actions.length > 0 && (
        <section className="adv-context-card adv-prefill">
          <span className="adv-label">Use Andersen data</span>
          {actions.map((a) => (
            <div key={a.componentType} className="adv-prefill-action">
              <p className="muted">{a.description}</p>
              <button
                type="button"
                className="btn btn-ghost adv-btn"
                onClick={() => onPrefill(a.componentType)}
                disabled={busy !== null}
              >
                {busy === a.componentType ? "Loading…" : a.label}
              </button>
            </div>
          ))}
          {error && <p className="adv-inline-error">{error}</p>}
        </section>
      )}

      {err && <div className="banner-error">{err}</div>}
      {!ctx && !err && <p className="muted">Loading client…</p>}
      {ctx && (
        <>
          <section className="adv-context-card">
            <div className="adv-context-name">
              <ClientTypeIcon type={ctx.investor.type} className="type-icon" />
              <Link to={`/app/crm/${ctx.investor.id}`} className="adv-context-link">
                {ctx.investor.name}
              </Link>
            </div>
            <div className="adv-context-pills">
              <span className={`pill stage-${ctx.investor.stage}`}>{ctx.investor.stage}</span>
              <span className={`pill ${KYC_PILL[ctx.investor.kyc_status] ?? "kyc-none"}`}>KYC {ctx.investor.kyc_status}</span>
            </div>
            <dl className="adv-context-facts">
              <dt>Risk profile</dt>
              <dd>{ctx.investor.risk_profile != null ? `${ctx.investor.risk_profile} of 5` : "Not set"}</dd>
              {ctx.investor.kyc_reviewed_at && (
                <>
                  <dt>KYC reviewed</dt>
                  <dd>{dateOnly(ctx.investor.kyc_reviewed_at)}</dd>
                </>
              )}
              {ctx.investor.city && (
                <>
                  <dt>City</dt>
                  <dd>{ctx.investor.city}</dd>
                </>
              )}
            </dl>
          </section>

          <section className="adv-context-card">
            <span className="adv-label">With Andersen today</span>
            {ctx.portfolio && ctx.portfolio.accounts.length > 0 ? (
              <>
                <table className="table adv-table-mini">
                  <tbody>
                    {ctx.portfolio.accounts.map((a) => (
                      <tr key={a.id}>
                        <td>
                          {a.name}
                          <div className="muted adv-isin">
                            {a.positions.length} holdings · cash {money(a.cash_balance, a.currency)}
                          </div>
                        </td>
                        <td className="num">{money(a.value, a.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="num">{money(ctx.portfolio.nok?.total_nok ?? ctx.portfolio.total_value, "NOK")}</td>
                    </tr>
                  </tfoot>
                </table>
                <div className="perf-strip adv-perf-mini">
                  <div className="perf-figure">
                    <span className="label">Return</span>
                    <span className={ctx.portfolio.total_return_pct >= 0 ? "value up" : "value down"}>
                      {pct(ctx.portfolio.total_return_pct, 1)}
                    </span>
                  </div>
                  <div className="perf-figure">
                    <span className="label">Open orders</span>
                    <span className="value">{ctx.openOrders.length}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="muted">No accounts with Andersen yet.</p>
            )}
          </section>

          {ctx.recentActivities.length > 0 && (
            <section className="adv-context-card">
              <span className="adv-label">Recent contact</span>
              <ul className="adv-context-list">
                {ctx.recentActivities.slice(0, 3).map((a) => (
                  <li key={a.id}>
                    <span className={`pill kind-${a.kind}`}>{a.kind}</span>
                    <span className="adv-context-subject">{a.subject}</span>
                    <span className="muted">{dateOnly(a.occurred_at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </aside>
  );
}
